import { useSyncExternalStore } from 'react'

export const LOCAL_STORAGE_KEYS = {
  'okami-theme': '@okami-web:theme',
  'okami-collection-id': '@okami-web:collection-id',
} as const

export type OkamiLocalStorageType = {
  'okami-refresh-token': string
  'okami-theme': 'dark' | 'light'
  'okami-token': string
  'okami-collection-id': string | ''
}

export type LocalStorageKeys = typeof LOCAL_STORAGE_KEYS

const storageService = {
  get(key: keyof LocalStorageKeys): OkamiLocalStorageType[typeof key] | null {
    const value = localStorage.getItem(LOCAL_STORAGE_KEYS[key])

    if (!value) {
      return null
    }

    try {
      return JSON.parse(value) as OkamiLocalStorageType[typeof key]
    } catch (e) {
      return value
    }
  },

  set(key: keyof LocalStorageKeys, value: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS[key], JSON.stringify(value))
  },

  delete(key: keyof LocalStorageKeys) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS[key])
  },
}

function useLocalStorage(key: keyof LocalStorageKeys) {
  const ac = new AbortController()

  function handleStorageChange(onStoreChange: () => void) {
    window.addEventListener(
      'storage',
      (event) => {
        if (event.key === LOCAL_STORAGE_KEYS[key]) {
          onStoreChange()
        }
      },
      {
        signal: ac.signal,
      },
    )

    return () => {
      ac.abort()
    }
  }

  const getCurrentValue = () => storageService.get(key) ?? null

  return useSyncExternalStore(handleStorageChange, getCurrentValue)
}

export { storageService, useLocalStorage }
